import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from '@heartinternet/hi-ui';
import { Text } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Spinner } from '@heartinternet/hi-ui';
import { GradientBar } from '@heartinternet/hi-ui';
import { fetchJson } from '@heartinternet/hi-ui';
import { Icon } from '@heartinternet/hi-ui';
import type {
  FeatureComparisonRow,
  PlanFeatures,
  ServiceType,
  UpgradeCurrentPlan,
  UpgradePlan,
  UpgradesResponse,
} from '../types';
import './UpgradeModal.css';

const SERVICES_ENDPOINT = '/cp/api/v1/services/';
const BASKET_URL = '/manage/basket.cgi';

// Only these feature keys from BE config render in the comparison section.
// Keys are in display order; anything else BE sends is ignored.
const FEATURE_DISPLAY_ORDER = ['site_limit', 'storage', 'mailbox_count'] as const;

const FEATURE_LABELS: Record<ServiceType, Record<string, string>> = {
  cpanel: {
    site_limit: 'Websites',
    storage: 'NVMe U.3 storage',
    mailbox_count: 'Mailboxes',
  },
  'managed-wordpress': {
    site_limit: 'WordPress websites',
    storage: 'NVMe U.3 storage',
    mailbox_count: 'Mailboxes',
  },
};

function buildComparisonRows(
  serviceType: ServiceType,
  current: PlanFeatures,
  upgraded: PlanFeatures
): FeatureComparisonRow[] {
  const labels = FEATURE_LABELS[serviceType];
  const rows: FeatureComparisonRow[] = [];
  for (const key of FEATURE_DISPLAY_ORDER) {
    const currentVal = current[key];
    const upgradedVal = upgraded[key];
    if (currentVal == null && upgradedVal == null) continue;
    rows.push({
      key,
      label: labels[key],
      current: currentVal != null ? String(currentVal) : '—',
      upgraded: upgradedVal != null ? String(upgradedVal) : '—',
    });
  }
  return rows;
}

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceId: string;
  serviceType: ServiceType;
  currentPlanType: string;
  renewalDate: string;
  isWordpress: boolean;
  trustpilotToken?: string;
  trustpilotTemplateId?: string;
  trustpilotBusinessUnitId?: string;
}

const formatPrice = (price: string) => {
  const num = parseFloat(price);
  return isNaN(num) ? price : `£${num.toFixed(2)}`;
};

// Backend sends ISO YYYY-MM-DD; render as DD/MM/YY.
const formatRenewalDate = (date: string) => {
  const parts = date.split('-');
  if (parts.length !== 3) return date;
  const [year, month, day] = parts;
  return `${day}/${month}/${year.slice(-2)}`;
};

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  serviceId,
  serviceType,
  currentPlanType,
  renewalDate,
  isWordpress,
  trustpilotToken,
  trustpilotTemplateId,
  trustpilotBusinessUnitId,
}) => {
  const [current, setCurrent] = useState<UpgradeCurrentPlan | null>(null);
  const [plans, setPlans] = useState<UpgradePlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittingPlanType, setSubmittingPlanType] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchUpgrades = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    setCurrent(null);
    setPlans([]);

    const { data, error: fetchError } = await fetchJson<UpgradesResponse>(
      `${SERVICES_ENDPOINT}${serviceType}/${serviceId}/upgrades`,
      { signal: controller.signal }
    );

    if (controller.signal.aborted) return;

    if (fetchError || !data) {
      setError(fetchError || 'Failed to load upgrade options.');
      setLoading(false);
      return;
    }

    setCurrent(data.current);
    setPlans(data.upgrades);
    setLoading(false);
  }, [serviceId, serviceType]);

  useEffect(() => {
    if (!isOpen) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: load upgrade options when the modal transitions to open
    fetchUpgrades();
    return () => {
      abortRef.current?.abort();
    };
  }, [isOpen, fetchUpgrades]);

  const handleUpgradeClick = (plan: UpgradePlan) => {
    setSubmittingPlanType(plan.new_type);
    // eslint-disable-next-line
    window.location.href = `${BASKET_URL}?type=MULTI&submit-type=PURCHASE&basket-add-code=${plan.new_type}-${plan.payment_interval}-card&upgrade_for=${serviceId}`;
  };

  const handleClose = () => {
    setCurrent(null);
    setPlans([]);
    setError(null);
    setLoading(false);
    setSubmittingPlanType(null);
    onClose();
  };

  const formatType = (type: string) => (type ? type.replace(/\./g, ' ') : '');
  const prefix = isWordpress ? 'WP' : 'CP';

  // Footer support prompt (issue #198): cPanel upgraders are pointed at Managed
  // WordPress; non-Agency MWP upgraders at the Agency plan; Agency MWP plans get
  // no prompt (there's nothing higher to point them at).
  const isAgency = /agency/i.test(currentPlanType);
  const upgradePrompt =
    serviceType === 'cpanel'
      ? 'Looking for Managed WordPress instead?'
      : isAgency
        ? null
        : 'Looking for an Agency plan instead?';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="medium" title="">
      <div
        className={`hi-ui-upgrade-modal hi-ui-upgrade-modal--plans-${Math.min(plans.length, 3)}`}
      >
        <div className="hi-ui-upgrade-modal__header">
          <div className="hi-ui-upgrade-modal__icon">
            <svg
              width="82"
              height="34"
              viewBox="0 0 82 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <mask
                id="upgrade-icon-mask"
                maskUnits="userSpaceOnUse"
                x="24"
                y="0"
                width="34"
                height="34"
                fill="black"
              >
                <rect fill="white" x="24" width="34" height="34" />
                <path d="M41 3C48.732 3 55 9.26801 55 17C55 24.732 48.732 31 41 31C33.268 31 27 24.732 27 17C27 9.26801 33.268 3 41 3ZM38.3711 21.3711C37.4046 21.3711 36.6211 22.1546 36.6211 23.1211C36.6211 24.0876 37.4046 24.8711 38.3711 24.8711H43.6211C44.5876 24.8711 45.3711 24.0876 45.3711 23.1211C45.3711 22.1546 44.5876 21.3711 43.6211 21.3711H38.3711ZM40.9336 9.83008C40.4696 9.80976 39.9988 9.97488 39.6445 10.3291L34.3291 15.6445C33.6589 16.3147 33.6589 17.4021 34.3291 18.0723C34.9992 18.7422 36.0857 18.742 36.7559 18.0723L40.9219 13.9053L45.0898 18.0732C45.76 18.7434 46.8474 18.7434 47.5176 18.0732C48.1875 17.4032 48.1873 16.3167 47.5176 15.6465L42.2021 10.3311C41.8531 9.98203 41.3909 9.81558 40.9336 9.83008Z" />
              </mask>
              <path
                d="M36.6211 23.1211H33.6211V23.1211H36.6211ZM45.3711 23.1211H48.3711V23.1211H45.3711ZM40.9336 9.83008L40.8024 12.8272L40.9155 12.8322L41.0287 12.8286L40.9336 9.83008ZM39.6445 10.3291L37.5234 8.20755L37.5232 8.20778L39.6445 10.3291ZM34.3291 15.6445L32.2078 13.5232V13.5232L34.3291 15.6445ZM34.3291 18.0723L32.2078 20.1936L32.208 20.1938L34.3291 18.0723ZM36.7559 18.0723L38.8764 20.1944L38.8774 20.1933L36.7559 18.0723ZM40.9219 13.9053L43.0432 11.784L40.9216 9.66238L38.8003 11.7842L40.9219 13.9053ZM47.5176 18.0732L49.6389 20.1946L49.6391 20.1943L47.5176 18.0732ZM47.5176 15.6465L49.6397 13.5259L49.6389 13.5252L47.5176 15.6465ZM42.2021 10.3311L44.3235 8.20973L42.2021 10.3311ZM41 3V6C47.0751 6 52 10.9249 52 17H55H58C58 7.61116 50.3888 0 41 0V3ZM55 17H52C52 23.0751 47.0751 28 41 28V31V34C50.3888 34 58 26.3888 58 17H55ZM41 31V28C34.9249 28 30 23.0751 30 17H27H24C24 26.3888 31.6112 34 41 34V31ZM27 17H30C30 10.9249 34.9249 6 41 6V3V0C31.6112 0 24 7.61116 24 17H27ZM38.3711 21.3711V18.3711C35.7477 18.3711 33.6211 20.4977 33.6211 23.1211H36.6211H39.6211C39.6211 23.8114 39.0615 24.3711 38.3711 24.3711V21.3711ZM36.6211 23.1211H33.6211C33.6211 25.7444 35.7477 27.8711 38.3711 27.8711V24.8711V21.8711C39.0615 21.8711 39.6211 22.4307 39.6211 23.1211H36.6211ZM38.3711 24.8711V27.8711H43.6211V24.8711V21.8711H38.3711V24.8711ZM43.6211 24.8711V27.8711C46.2444 27.8711 48.3711 25.7444 48.3711 23.1211H45.3711H42.3711C42.3711 22.4307 42.9307 21.8711 43.6211 21.8711V24.8711ZM45.3711 23.1211H48.3711C48.3711 20.4977 46.2444 18.3711 43.6211 18.3711V21.3711V24.3711C42.9307 24.3711 42.3711 23.8114 42.3711 23.1211H45.3711ZM43.6211 21.3711V18.3711H38.3711V21.3711V24.3711H43.6211V21.3711ZM40.9336 9.83008L41.0648 6.83295C39.8043 6.77776 38.5029 7.22835 37.5234 8.20755L39.6445 10.3291L41.7656 12.4507C41.4948 12.7214 41.135 12.8418 40.8024 12.8272L40.9336 9.83008ZM39.6445 10.3291L37.5232 8.20778L32.2078 13.5232L34.3291 15.6445L36.4504 17.7659L41.7659 12.4504L39.6445 10.3291ZM34.3291 15.6445L32.2078 13.5232C30.366 15.365 30.366 18.3518 32.2078 20.1936L34.3291 18.0723L36.4504 15.9509C36.9518 16.4523 36.9518 17.2645 36.4504 17.7659L34.3291 15.6445ZM34.3291 18.0723L32.208 20.1938C34.0501 22.0355 37.0354 22.0341 38.8764 20.1944L36.7559 18.0723L34.6353 15.9502C35.136 15.4499 35.9482 15.4488 36.4502 15.9507L34.3291 18.0723ZM36.7559 18.0723L38.8774 20.1933L43.0434 16.0263L40.9219 13.9053L38.8003 11.7842L34.6343 15.9512L36.7559 18.0723ZM40.9219 13.9053L38.8006 16.0266L42.9685 20.1946L45.0898 18.0732L47.2112 15.9519L43.0432 11.784L40.9219 13.9053ZM45.0898 18.0732L42.9685 20.1946C44.8103 22.0363 47.7971 22.0363 49.6389 20.1946L47.5176 18.0732L45.3963 15.9519C45.8976 15.4506 46.7098 15.4506 47.2112 15.9519L45.0898 18.0732ZM47.5176 18.0732L49.6391 20.1943C51.4809 18.3522 51.4794 15.367 49.6397 13.5259L47.5176 15.6465L45.3955 17.767C44.8952 17.2664 44.8941 16.4541 45.396 15.9521L47.5176 18.0732ZM47.5176 15.6465L49.6389 13.5252L44.3235 8.20973L42.2021 10.3311L40.0808 12.4524L45.3963 17.7678L47.5176 15.6465ZM42.2021 10.3311L44.3235 8.20973C43.3616 7.2479 42.0853 6.79206 40.8385 6.83158L40.9336 9.83008L41.0287 12.8286C40.6965 12.8391 40.3446 12.7162 40.0808 12.4524L42.2021 10.3311Z"
                fill="url(#upgrade-icon-gradient)"
                mask="url(#upgrade-icon-mask)"
              />
              <line
                x1="63.5"
                y1="18.5"
                x2="80.5"
                y2="18.5"
                stroke="#D9D9D9"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <line
                x1="1.5"
                y1="18.5"
                x2="18.5"
                y2="18.5"
                stroke="#D9D9D9"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <defs>
                <linearGradient
                  id="upgrade-icon-gradient"
                  x1="41"
                  y1="3"
                  x2="41"
                  y2="31"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#256957" />
                  <stop offset="1" stopColor="#193252" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <Text variant="heading-3" className="hi-ui-upgrade-modal__title">
            Upgrade your {prefix}{' '}
            <span className="hi-ui-package-table__package-badge-border">
              <span className="hi-ui-package-table__package-badge">
                {formatType(currentPlanType)}
              </span>
            </span>{' '}
            package
          </Text>
          <Text variant="body-small" className="hi-ui-upgrade-modal__subtitle">
            Get more sites, storage, and mailboxes instantly.
          </Text>
          <Text variant="body-small" className="hi-ui-upgrade-modal__green-text">
            You&apos;ll only pay the difference until your next renewal date.
          </Text>
        </div>

        <div className="hi-ui-upgrade-modal__body">
          {loading && (
            <div className="hi-ui-upgrade-modal__loading">
              <Spinner size="large" aria-label="Loading upgrade options" />
            </div>
          )}

          {error && (
            <div className="hi-ui-upgrade-modal__error">
              <Notice variant="error">{error}</Notice>
              <Button variant="secondary" onClick={fetchUpgrades}>
                Try again
              </Button>
            </div>
          )}

          {!loading && !error && current && plans.length === 0 && (
            <Notice variant="info">No upgrade options are available for this plan.</Notice>
          )}

          {!loading && !error && current && plans.length > 0 && (
            <div className="hi-ui-upgrade-modal__plans">
              {plans.map((plan) => {
                const rows = buildComparisonRows(serviceType, current.features, plan.features);
                return (
                  <div key={plan.new_type} className="hi-ui-upgrade-modal__card">
                    <Text variant="heading-4" className="hi-ui-upgrade-modal__card-name">
                      {plan.name}
                    </Text>
                    <GradientBar className="hi-ui-upgrade-modal__card-bar" />

                    {rows.length > 0 && (
                      <div className="hi-ui-upgrade-modal__features">
                        {rows.map((row) => (
                          <div key={row.key} className="hi-ui-upgrade-modal__feature">
                            <Icon
                              name="check-circle-grey"
                              size={20}
                              className="hi-ui-upgrade-modal__feature-tick"
                            />
                            <div>
                              <Text
                                variant="body-small"
                                bold
                                inline
                                className="hi-ui-upgrade-modal__feature-label"
                              >
                                {row.label}
                              </Text>
                              <div className="hi-ui-upgrade-modal__feature-values">
                                <span className="hi-ui-upgrade-modal__feature-old">
                                  {row.current}
                                </span>
                                <Icon
                                  name="arrow-right-green"
                                  size={14}
                                  className="hi-ui-upgrade-modal__feature-arrow"
                                />
                                <span className="hi-ui-upgrade-modal__feature-new">
                                  {row.upgraded}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="hi-ui-upgrade-modal__pricing">
                      <Text variant="heading-3" className="hi-ui-upgrade-modal__price">
                        {formatPrice(plan.prorated_charge_exvat)} today
                      </Text>
                      <Text variant="body-small" inline className="hi-ui-upgrade-modal__vat">
                        {typeof plan.tax_rate === 'number' ? `+${plan.tax_rate}% VAT` : '+VAT'}
                      </Text>
                    </div>

                    <Button
                      variant="primary"
                      size="large"
                      onClick={() => handleUpgradeClick(plan)}
                      loading={submittingPlanType === plan.new_type}
                      disabled={Boolean(submittingPlanType) && submittingPlanType !== plan.new_type}
                      className="hi-ui-upgrade-modal__cta"
                    >
                      UPGRADE NOW
                    </Button>
                    <Text variant="body-small" inline className="hi-ui-upgrade-modal__renewal">
                      {formatPrice(plan.full_price_exvat)} (+vat) at next renewal
                      {renewalDate ? ` on ${formatRenewalDate(renewalDate)}` : ''}
                    </Text>
                  </div>
                );
              })}
            </div>
          )}

          <div className="hi-ui-upgrade-modal__footer">
            {trustpilotToken && (
              <div
                ref={(el) => {
                  const tp = (window as unknown as Record<string, unknown>).Trustpilot as
                    { loadFromElement: (el: HTMLElement) => void } | undefined;
                  if (el && tp?.loadFromElement) {
                    tp.loadFromElement(el);
                  }
                }}
                className="trustpilot-widget"
                data-locale="en-US"
                data-template-id={trustpilotTemplateId}
                data-businessunit-id={trustpilotBusinessUnitId}
                data-style-height="24px"
                data-style-width="100%"
                data-token={trustpilotToken}
                data-min-review-count="0"
                data-style-alignment="center"
              >
                <a
                  href="https://uk.trustpilot.com/review/www.heartinternet.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'none' }}
                >
                  Trustpilot
                </a>
              </div>
            )}
            {upgradePrompt && (
              <Text variant="body-small">
                {upgradePrompt}{' '}
                <Link href="https://heartinternet.uk/support/" external>
                  Contact our support team.
                </Link>
              </Text>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
